import React, { useState, useRef, useImperativeHandle, forwardRef, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Stack,
  MenuItem,
  Menu,
  Chip,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Clear as ClearIcon,
  EventAvailable as EventAvailableIcon,
  NoteAdd as NoteAddIcon,
} from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  ColumnDef,
  flexRender,
  SortingState,
  PaginationState,
} from '@tanstack/react-table';
import { AcquisitionSource, AdminCompanyListItem, Company, PaginatedResponse, SearchParams, SubscriptionStatus } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import DataTableHeaderCell from '../common/DataTableHeaderCell';
import PageHeader from '../common/PageHeader';
import PageHeaderActionButton from '../common/PageHeaderActionButton';
import PaginationFooter from '../common/PaginationFooter';
import ListSurface from '../common/ListSurface';
import ColumnVisibilityMenu from '../common/ColumnVisibilityMenu';
import NotebookColumnTab from '../common/NotebookColumnTab';
import IdentityAvatar from '../common/IdentityAvatar';
import CompanyNameCell from '../common/CompanyNameCell';
import { getColumnKey } from '../../utils/table';
import { sortingFromTableSort, tableSortFromSorting } from '../../utils/tableSort';
import RowActionsButton from '../common/RowActionsButton';

interface CompanyListProps {
  paginatedData: PaginatedResponse<AdminCompanyListItem> | null;
  loading: boolean;
  searchParams: SearchParams;
  onSearch: (name?: string) => void;
  onPaginationModelChange: (model: { page: number, pageSize: number }) => void;
  onSortChange: (field?: string, direction?: 'asc' | 'desc') => void;
  onEdit: (company: Company) => void;
  onDelete?: (company: Company) => void;
  onAdd: () => void;
  onManageSubscription?: (company: Company) => void;
  onManageNotes?: (company: Company) => void;
  onFilterChange?: (filters: Pick<SearchParams, 'effectiveStatus' | 'expiresWithinDays' | 'usageStatus'>) => void;
}

export interface CompanyListRef {
  focusSearch: () => void;
}

const CompanyList = forwardRef<CompanyListRef, CompanyListProps>(({
  paginatedData,
  loading,
  searchParams,
  onSearch,
  onPaginationModelChange,
  onSortChange,
  onEdit,
  onDelete,
  onAdd,
  onManageSubscription,
  onManageNotes,
  onFilterChange,
}, ref) => {
  const { t } = useLanguage();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTerm = searchParams.name || '';
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuCompany, setMenuCompany] = useState<Company | null>(null);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<HTMLElement | null>(null);
  const [columnVisibility, setColumnVisibility] = useState({
    subscriptionStatus: true,
    subscriptionEnds: true,
    userCount: true,
    lastActivity: true,
    email: true,
    phone: true,
    address: false,
    acquisitionSource: false,
  });
  const closeMenu = () => { setMenuAnchor(null); setMenuCompany(null); };
  const openMenu = (event: React.MouseEvent<HTMLElement>, company: Company) => { setMenuAnchor(event.currentTarget); setMenuCompany(company); };
  const columnWidths = {
    name: '22%',
    subscriptionStatus: '13%',
    subscriptionEnds: '13%',
    userCount: '9%',
    lastActivity: '14%',
    email: '21%',
    phone: '15%',
    address: '18%',
    acquisitionSource: '12%',
    actions: '12%',
  } as const;

  const statusLabel = useCallback((status?: SubscriptionStatus) => {
    if (status === SubscriptionStatus.SUSPENDED) return t('subscription.suspended');
    if (status === SubscriptionStatus.READ_ONLY) return t('subscription.readOnly');
    return t('subscription.active');
  }, [t]);

  const usageLabel = useCallback((usage?: Company['usageStatus']) => {
    if (usage === 'NEVER_USED') return t('companies.usageNever');
    if (usage === 'INACTIVE_30D') return t('companies.usageInactive');
    return t('companies.usageActive');
  }, [t]);

  const formatDate = (value?: string | null) => value ? new Date(value).toLocaleDateString() : '—';

  const acquisitionLabel = useCallback((source?: AcquisitionSource) => {
    switch (source) {
      case AcquisitionSource.SELF_SIGNUP_REFERRAL: return t('companies.sourceReferral');
      case AcquisitionSource.SELF_SIGNUP_ORGANIC: return t('companies.sourceOrganic');
      case AcquisitionSource.ADMIN_CREATED: return t('companies.sourceAdmin');
      default: return t('companies.sourceLegacy');
    }
  }, [t]);

  useImperativeHandle(ref, () => ({
    focusSearch: () => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  }));

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSearch(event.target.value || undefined);
  };

  const handleClearSearch = () => {
    onSearch(undefined);
  };

  // Define table columns using TanStack React Table ColumnDef
  const allColumns = useMemo<ColumnDef<Company>[]>(() => [
    {
      accessorKey: 'name',
      header: t('companies.companyName'),
      cell: ({ row }) => <CompanyNameCell name={row.original.name} companyId={row.original.id} />,
    },
    {
      id: 'subscriptionStatus',
      header: t('companies.subscriptionStatus'),
      cell: ({ row }) => <Chip size="small" color={row.original.subscription?.effectiveStatus === SubscriptionStatus.ACTIVE ? 'success' : 'warning'} label={statusLabel(row.original.subscription?.effectiveStatus)} />,
    },
    {
      id: 'subscriptionEnds',
      header: t('companies.subscriptionEnds'),
      cell: ({ row }) => <Typography variant="body2" color="text.secondary">
        {row.original.subscription?.endsAt
          ? `${formatDate(row.original.subscription.endsAt)}${row.original.subscription.remainingDays !== null && row.original.subscription.remainingDays !== undefined ? ` (${row.original.subscription.remainingDays} ${t('companies.days')})` : ''}`
          : t('companies.unlimited')}
      </Typography>,
    },
    {
      id: 'userCount',
      header: t('companies.usersCount'),
      cell: ({ row }) => <Typography variant="body2">{row.original.userCount ?? '—'}</Typography>,
    },
    {
      id: 'lastActivity',
      header: t('companies.lastActivity'),
      cell: ({ row }) => <Box>
        <Typography variant="body2">{formatDate(row.original.lastBusinessActivityAt)}</Typography>
        {row.original.usageStatus && <Typography variant="caption" color="text.secondary">{usageLabel(row.original.usageStatus)}</Typography>}
      </Box>,
    },
    {
      accessorKey: 'email',
      header: t('common.email'),
      cell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <EmailIcon fontSize="small" color="action" />
          <Typography variant="body2" noWrap sx={{ minWidth: 0, textOverflow: 'ellipsis', overflow: 'hidden' }}>
            {row.original.email}
          </Typography>
        </Box>
      ),
    },
    {
      accessorKey: 'phone',
      header: t('common.phone'),
      cell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <PhoneIcon fontSize="small" color="action" />
          <Typography variant="body2" noWrap sx={{ minWidth: 0, textOverflow: 'ellipsis', overflow: 'hidden' }}>
            {row.original.phone}
          </Typography>
        </Box>
      ),
    },
    {
      accessorKey: 'address',
      header: t('common.address'),
      cell: ({ row }) => (
        <Typography variant="body2" noWrap sx={{ minWidth: 0, textOverflow: 'ellipsis', overflow: 'hidden' }}>
          {row.original.address}
        </Typography>
      ),
    },
    {
      accessorKey: 'acquisitionSource',
      header: t('companies.source'),
      cell: ({ row }) => <Typography variant="body2" noWrap color="text.secondary">{acquisitionLabel(row.original.acquisitionSource)}</Typography>,
    },
    {
      id: 'actions',
      header: t('common.actions'),
      cell: ({ row }) => (
        <RowActionsButton aria-label={`${t('common.actions')}: ${row.original.name}`} aria-haspopup="menu" onClick={event => openMenu(event, row.original)} />
      ),
    },
  ], [t, acquisitionLabel, statusLabel, usageLabel]);
  const columns = useMemo(() => allColumns.filter(column => {
    const key = getColumnKey(column);
    return !key || !Object.prototype.hasOwnProperty.call(columnVisibility, key) || columnVisibility[key as keyof typeof columnVisibility];
  }), [allColumns, columnVisibility]);

  // TanStack React Table configuration
  const [sorting, setSorting] = useState<SortingState>(() => sortingFromTableSort(searchParams));

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: searchParams.page ?? 0,
    pageSize: searchParams.size ?? 10,
  });

  const table = useReactTable({
    data: paginatedData?.content || [],
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);
      const nextSort = tableSortFromSorting(newSorting);
      onSortChange(nextSort.sort, nextSort.direction);
    },
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
      setPagination(newPagination);
      onPaginationModelChange({
        page: newPagination.pageIndex,
        pageSize: newPagination.pageSize,
      });
    },
    getCoreRowModel: getCoreRowModel(),
    sortDescFirst: false,
    manualPagination: true,
    manualSorting: true,
    pageCount: paginatedData?.totalPages || 0,
    rowCount: paginatedData?.totalElements || 0,
  });

  // Sync external state changes to table state
  React.useEffect(() => {
    setPagination({
      pageIndex: searchParams.page ?? 0,
      pageSize: searchParams.size ?? 10,
    });
  }, [searchParams.page, searchParams.size]);

  React.useEffect(() => {
    setSorting(sortingFromTableSort(searchParams));
  }, [searchParams]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', pb: { xs: 'calc(61px + env(safe-area-inset-bottom))', md: 0 } }}>
      <PageHeader
        title={t('companies.title')}
        icon={<BusinessIcon />}
        accent="companies"
        description={t('companies.subtitle')}
        mobileMode="hidden"
        actions={<PageHeaderActionButton startIcon={<AddIcon />} onClick={onAdd}>{t('companies.addCompany')}</PageHeaderActionButton>}
      />
      <ListSurface sx={{ mt: { xs: 0, md: 2 }, overflow: { xs: 'hidden', md: 'visible' } }}>
        <ListSurface.Toolbar sx={{ p: { xs: 1.5, md: 2.5 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <TextField
            inputRef={searchInputRef}
            placeholder={t('companies.searchPlaceholder')}
            aria-label={t('companies.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            fullWidth
            sx={{
              flex: 1,
              minWidth: 0,
              '& .MuiOutlinedInput-root': {
                height: { xs: 44, md: 48 },
                borderRadius: { xs: 2, md: 1 },
                bgcolor: { xs: 'action.hover', md: 'background.paper' },
                '& fieldset, &:hover fieldset': { borderColor: { xs: 'transparent', md: 'divider' } },
                '&.Mui-focused fieldset': { border: '2px solid', borderColor: 'primary.main' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton aria-label={t('companies.clearSearch')} size="small" onClick={handleClearSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <IconButton aria-label={t('companies.addCompany')} onClick={onAdd} sx={{ display: { xs: 'inline-flex', md: 'none' }, width: 44, height: 44, flexShrink: 0, color: 'primary.main' }}><AddIcon sx={{ fontSize: 30 }} /></IconButton>
          </Stack>
          {onFilterChange && <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 } }}>
              <InputLabel>{t('companies.subscriptionStatus')}</InputLabel>
              <Select
                value={searchParams.effectiveStatus || ''}
                label={t('companies.subscriptionStatus')}
                onChange={event => onFilterChange({ effectiveStatus: (event.target.value || undefined) as SubscriptionStatus | undefined, expiresWithinDays: searchParams.expiresWithinDays, usageStatus: searchParams.usageStatus })}
              >
                <MenuItem value="">{t('companies.allStatuses')}</MenuItem>
                <MenuItem value={SubscriptionStatus.ACTIVE}>{t('subscription.active')}</MenuItem>
                <MenuItem value={SubscriptionStatus.READ_ONLY}>{t('subscription.readOnly')}</MenuItem>
                <MenuItem value={SubscriptionStatus.SUSPENDED}>{t('subscription.suspended')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 } }}>
              <InputLabel>{t('companies.usageStatus')}</InputLabel>
              <Select
                value={searchParams.usageStatus || ''}
                label={t('companies.usageStatus')}
                onChange={event => onFilterChange({ effectiveStatus: searchParams.effectiveStatus, expiresWithinDays: searchParams.expiresWithinDays, usageStatus: (event.target.value || undefined) as SearchParams['usageStatus'] })}
              >
                <MenuItem value="">{t('companies.allUsageStatuses')}</MenuItem>
                <MenuItem value="ACTIVE_30D">{t('companies.usageActive')}</MenuItem>
                <MenuItem value="INACTIVE_30D">{t('companies.usageInactive')}</MenuItem>
                <MenuItem value="NEVER_USED">{t('companies.usageNever')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 } }}>
              <InputLabel>{t('companies.expiringFilter')}</InputLabel>
              <Select
                value={searchParams.expiresWithinDays || ''}
                label={t('companies.expiringFilter')}
                onChange={event => onFilterChange({ effectiveStatus: searchParams.effectiveStatus, expiresWithinDays: event.target.value ? Number(event.target.value) as 7 | 30 : undefined, usageStatus: searchParams.usageStatus })}
              >
                <MenuItem value="">{t('companies.allExpiry')}</MenuItem>
                <MenuItem value={7}>{t('companies.expiring7')}</MenuItem>
                <MenuItem value={30}>{t('companies.expiring30')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>}
        </ListSurface.Toolbar>

        <ListSurface.Content sx={{ position: 'relative' }}>
        <NotebookColumnTab label={t('common.columns')} accent="companies" open={Boolean(columnMenuAnchor)} onClick={event => setColumnMenuAnchor(event.currentTarget)} />
        <TableContainer sx={{ overflowX: 'auto', display: { xs: 'none', md: 'block' } }}>
          <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
            <colgroup>
              {columns.map(column => <col key={getColumnKey(column)} style={{ width: columnWidths[getColumnKey(column) as keyof typeof columnWidths] }} />)}
            </colgroup>
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <DataTableHeaderCell
                      key={header.id}
                      align={header.column.id === 'actions' ? 'right' : 'left'}
                      sx={{
                        width: columnWidths[header.column.id as keyof typeof columnWidths],
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      }}
                      sort={header.column.getCanSort() ? {
                        active: Boolean(header.column.getIsSorted()),
                        direction: header.column.getIsSorted() === 'desc' ? 'desc' : 'asc',
                        onClick: () => header.column.toggleSorting(),
                      } : undefined}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </DataTableHeaderCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm ? t('companies.noSearchResults') : t('companies.noCompanies')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} hover>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        align={cell.column.id === 'actions' ? 'right' : 'left'}
                        sx={{
                          width: columnWidths[cell.column.id as keyof typeof columnWidths],
                          overflow: 'hidden',
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box component="ul" sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', listStyle: 'none', m: 0, p: 0 }}>
          {loading && <Box component="li" sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}
          {!loading && !paginatedData?.content.length && <Box component="li" sx={{ textAlign: 'center', py: 4 }}><Typography color="text.secondary">{searchTerm ? t('companies.noSearchResults') : t('companies.noCompanies')}</Typography></Box>}
          {!loading && paginatedData?.content.map(company => <Box component="li" key={company.id} sx={{ borderTop: '1px solid', borderColor: 'divider', '&:first-of-type': { borderTop: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 72, pl: 2, pr: 1, gap: 1.25 }}>
              <IdentityAvatar><BusinessIcon fontSize="small" /></IdentityAvatar>
              <Box sx={{ flex: 1, minWidth: 0, py: 1 }}>
                <CompanyNameCell name={company.name} companyId={company.id} />
                {company.email && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, overflowWrap: 'anywhere' }}>{company.email}</Typography>}
                {company.phone && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>{company.phone}</Typography>}
                {company.address && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, overflowWrap: 'anywhere' }}>{company.address}</Typography>}
              </Box>
              <RowActionsButton aria-label={`${t('common.actions')}: ${company.name}`} aria-haspopup="menu" onClick={event => openMenu(event, company)} />
            </Box>
          </Box>)}
        </Box>
        </ListSurface.Content>

        {/* Custom Pagination */}
        <ListSurface.Footer>
        {paginatedData && paginatedData.totalElements > 0 && (
          <PaginationFooter
            pageIndex={pagination.pageIndex}
            pageSize={pagination.pageSize}
            totalPages={paginatedData.totalPages}
            totalElements={paginatedData.totalElements}
            showingLabel={t('common.showing')}
            ofLabel={t('common.of')}
            pageSizeLabel={t('common.rowsPerPage')}
            onPageChange={pageIndex => table.setPageIndex(pageIndex)}
            onPageSizeChange={pageSize => table.setPageSize(pageSize)}
            mobileCompact
          />
        )}
        </ListSurface.Footer>
      </ListSurface>

      <ColumnVisibilityMenu side anchorEl={columnMenuAnchor} onClose={() => setColumnMenuAnchor(null)} onToggle={key => setColumnVisibility(previous => ({ ...previous, [key]: !previous[key as keyof typeof previous] }))} items={[
        { key: 'subscriptionStatus', label: t('companies.subscriptionStatus'), visible: columnVisibility.subscriptionStatus },
        { key: 'subscriptionEnds', label: t('companies.subscriptionEnds'), visible: columnVisibility.subscriptionEnds },
        { key: 'userCount', label: t('companies.usersCount'), visible: columnVisibility.userCount },
        { key: 'lastActivity', label: t('companies.lastActivity'), visible: columnVisibility.lastActivity },
        { key: 'email', label: t('common.email'), visible: columnVisibility.email },
        { key: 'phone', label: t('common.phone'), visible: columnVisibility.phone },
        { key: 'address', label: t('common.address'), visible: columnVisibility.address },
        { key: 'acquisitionSource', label: t('companies.source'), visible: columnVisibility.acquisitionSource },
      ]} />

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        <MenuItem onClick={() => { if (menuCompany) onEdit(menuCompany); closeMenu(); }}><EditIcon fontSize="small" sx={{ mr: 1.25 }} />{t('common.edit')}</MenuItem>
        {onManageSubscription && <MenuItem onClick={() => { if (menuCompany) onManageSubscription(menuCompany); closeMenu(); }}><EventAvailableIcon fontSize="small" sx={{ mr: 1.25 }} />{t('subscription.manage')}</MenuItem>}
        {onManageNotes && <MenuItem onClick={() => { if (menuCompany) onManageNotes(menuCompany); closeMenu(); }}><NoteAddIcon fontSize="small" sx={{ mr: 1.25 }} />Görüşme notları</MenuItem>}
        {onDelete && <MenuItem onClick={() => { if (menuCompany) onDelete(menuCompany); closeMenu(); }} sx={{ color: 'error.main' }}><DeleteIcon fontSize="small" sx={{ mr: 1.25 }} />{t('common.delete')}</MenuItem>}
      </Menu>
    </Box>
  );
});

export default CompanyList;
